import React, { useEffect, useState } from "react";
import { Card, Nav, Pagination, Table } from "react-bootstrap";
import {
  TiArrowSortedDown,
  TiArrowSortedUp,
  TiArrowUnsorted,
} from "react-icons/ti";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TableSkeleton } from "../../skeleton/TableSkeleton";
import { showPageNumbers } from "../../utils/table";
import TranchAssetTable from "./TranchAssetTable";
import Select from "react-select";
import styles from "./index.module.css";
import clsx from "clsx";
import { fetchTrancheAssets } from "../../service/api";

const DEFAULT_ASSETS_PAGE_LIMIT = 5;

const paginationOptions = [
  { value: 10, label: "10" },
  { value: 25, label: "25" },
  { value: 50, label: "50" },
];

const TranchesTable = ({
  columns,
  rows,
  loading: parentLoading,
  currentPage,
  setCurrentPage,
  totalPages,
  itemsPerPage,
  setItemsPerPage,
  sort,
  setSort,
  assetsRefreshSignal,
}) => {
  const [expandedRow, setExpandedRow] = useState(null);
  const [assetsByTrancheId, setAssetsByTrancheId] = useState({});
  const [assetsLoadingByTrancheId, setAssetsLoadingByTrancheId] = useState({});
  const [assetsErrorByTrancheId, setAssetsErrorByTrancheId] = useState({});
  const [assetsTotalByTrancheId, setAssetsTotalByTrancheId] = useState({});

  const showPaginationNumbers = totalPages > 1;
  const itemsPerPageNum = Number(itemsPerPage) || 10;
  const disableVerticalScroll = itemsPerPageNum === 10;

  const handleItemsPerPageChange = (selectedOption) => {
    if (selectedOption) {
      setItemsPerPage(selectedOption.value);
      setCurrentPage(1);
    }
  };

  const sortableColumns = {
    display: "flex",
    gap: 4,
  };

  const changeOrder = (header) => {
    const newOrder = sort[header._id] === 1 ? -1 : 1;
    setSort({ [header._id]: newOrder });
  };

  const loadAssetsForTranche = async (trancheId, { force = false } = {}) => {
    if (!trancheId) return;
    if (!force && assetsByTrancheId[trancheId]) return; // cached
    if (assetsLoadingByTrancheId[trancheId]) return; // already fetching

    setAssetsLoadingByTrancheId((prev) => ({ ...prev, [trancheId]: true }));
    setAssetsErrorByTrancheId((prev) => ({ ...prev, [trancheId]: null }));

    try {
      const res = await fetchTrancheAssets(trancheId, {
        page: 1,
        limit: DEFAULT_ASSETS_PAGE_LIMIT,
      });
      const assets = res?.data?.assets || [];
      const totalDocs =
        typeof res?.data?.totalDocs === "number" ? res.data.totalDocs : assets.length;
      setAssetsByTrancheId((prev) => ({ ...prev, [trancheId]: assets }));
      setAssetsTotalByTrancheId((prev) => ({ ...prev, [trancheId]: totalDocs }));
    } catch (error) {
      console.error("Error fetching tranche assets:", error);
      setAssetsErrorByTrancheId((prev) => ({
        ...prev,
        [trancheId]: "Failed to load attached assets.",
      }));
      setAssetsByTrancheId((prev) => ({ ...prev, [trancheId]: [] }));
      setAssetsTotalByTrancheId((prev) => ({ ...prev, [trancheId]: 0 }));
    } finally {
      setAssetsLoadingByTrancheId((prev) => ({ ...prev, [trancheId]: false }));
    }
  };

  const toggleRow = async (rowId) => {
    const nextExpanded = expandedRow === rowId ? null : rowId;
    setExpandedRow(nextExpanded);
    if (!nextExpanded) return;
    await loadAssetsForTranche(nextExpanded);
  };

  useEffect(() => {
    const trancheId = assetsRefreshSignal?.trancheId;
    if (!trancheId) return;

    if (trancheId === expandedRow) {
      loadAssetsForTranche(trancheId, { force: true });
      return;
    }

    setAssetsByTrancheId((prev) => {
      if (!prev[trancheId]) return prev;
      const next = { ...prev };
      delete next[trancheId];
      return next;
    });
    setAssetsTotalByTrancheId((prev) => {
      if (prev[trancheId] === undefined) return prev;
      const next = { ...prev };
      delete next[trancheId];
      return next;
    });
    setAssetsErrorByTrancheId((prev) => {
      if (prev[trancheId] === undefined) return prev;
      const next = { ...prev };
      delete next[trancheId];
      return next;
    });
  }, [assetsRefreshSignal, expandedRow]);

  const getExpandedRowAssets = () => {
    if (!expandedRow) return [];
    if (assetsByTrancheId[expandedRow]) return assetsByTrancheId[expandedRow];
    const expandedRowData = rows.find((row) => row._id === expandedRow);
    return expandedRowData ? expandedRowData.assets : [];
  };

  const displayValue = (value) =>
    value === null || value === undefined || value === "" ? "-" : value;

  return (
    <>
      {parentLoading ? (
        <TableSkeleton />
      ) : (
        <div className="scrollable-table-container2 mx-3">
          <Card
            border="light"
            className="table-wrapper table-responsive shadow-sm"
          >
            <Card.Body className="p-4">
              <div
                className="table-scroll-container"
                style={
                  disableVerticalScroll
                    ? { overflowY: "visible", maxHeight: "none" }
                    : undefined
                }
              >
                <Table
                  hover
                  className="user-table align-items-center table-striped"
                >
                  <thead>
                    <tr>
                      {columns.map((header, index) => {
                        const isSortApplied = header.isSorting;
                        const sortIcon = !sort[header._id] ? (
                          <TiArrowUnsorted color="grey" size={16} />
                        ) : sort[header._id] === 1 ? (
                          <TiArrowSortedDown size={16} />
                        ) : (
                          <TiArrowSortedUp size={16} />
                        );

                        return isSortApplied ? (
                          <th key={index} onClick={() => changeOrder(header)}>
                            <div style={sortableColumns}>
                              {header.label}
                              {sortIcon}
                            </div>
                          </th>
                        ) : (
                          <th key={index}>{header.label}</th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {!rows.length ? (
                      <tr>
                        <td className="bg-grey" colSpan={columns.length}>
                          No Tranches Found.
                        </td>
                      </tr>
                    ) : (
                      rows.map((row, index) => (
                        <React.Fragment key={row._id}>
                          <tr
                            className={`${
                              index % 2 === 0 ? "even-row" : "odd-row"
                            } ${expandedRow === row._id ? "expanded" : ""}`}
                            onClick={() => toggleRow(row._id)}
                            style={{ cursor: "pointer" }}
                          >
                            {columns.map((col) => {
                              const cellValue =
                                displayValue(row[col._id]) || "-";
                              return (
                                <td key={col._id}>
                                  {col._id === "trancheId" ? (
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: "8px",
                                        alignItems: "center",
                                      }}
                                    >
                                      <FontAwesomeIcon
                                        icon={
                                          expandedRow === row._id
                                            ? faChevronUp
                                            : faChevronDown
                                        }
                                      />
                                      {cellValue}
                                    </div>
                                  ) : (
                                    cellValue
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                          {expandedRow === row._id && (
                            <tr>
                              <td colSpan={columns.length} className="p-0">
                                <div className="accordion-content p-3 bg-white px-5">
                                  {assetsLoadingByTrancheId[row._id] ? (
                                    <div className="text-muted mb-2">
                                      Loading assets…
                                    </div>
                                  ) : assetsErrorByTrancheId[row._id] ? (
                                    <div className="text-danger mb-2">
                                      {assetsErrorByTrancheId[row._id]}
                                    </div>
                                  ) : null}
                                  <TranchAssetTable
                                    trancheId={row._id}
                                    assets={getExpandedRowAssets()}
                                    totalDocs={
                                      typeof assetsTotalByTrancheId[row._id] === "number"
                                        ? assetsTotalByTrancheId[row._id]
                                        : getExpandedRowAssets().length
                                    }
                                  />
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
            <Card.Footer
              className={clsx(
                "px-3 border-0 d-lg-flex justify-content-between",
                styles.stickyFooter
              )}
            >
              <Nav>
                <Pagination className="mb-2 mb-lg-0">
                  <Pagination.Prev
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Pagination.Prev>
                  {showPaginationNumbers && (
                    <>
                      {currentPage > 4 && (
                        <>
                          <Pagination.Item onClick={() => setCurrentPage(1)}>
                            1
                          </Pagination.Item>
                          <Pagination.Ellipsis disabled />
                        </>
                      )}
                      {[...Array(totalPages)].map((_, page) => {
                        if (showPageNumbers(page, currentPage, totalPages)) {
                          return (
                            <Pagination.Item
                              key={page + 1}
                              active={page + 1 === currentPage}
                              onClick={() => setCurrentPage(page + 1)}
                            >
                              {page + 1}
                            </Pagination.Item>
                          );
                        }
                        return null;
                      })}
                      {currentPage < totalPages - 3 && (
                        <>
                          <Pagination.Ellipsis disabled />
                          <Pagination.Item
                            onClick={() => setCurrentPage(totalPages)}
                          >
                            {totalPages}
                          </Pagination.Item>
                        </>
                      )}
                    </>
                  )}
                  <Pagination.Next
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    disabled={currentPage === totalPages || totalPages === 0}
                  >
                    Next
                  </Pagination.Next>
                </Pagination>
              </Nav>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                Items Per Page
                <Select
                  options={paginationOptions}
                  defaultValue={paginationOptions.find(
                    (opt) => opt.value === itemsPerPage
                  )}
                  onChange={(selected) => handleItemsPerPageChange(selected)}
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minHeight: "32px",
                      height: "32px",
                      fontSize: "14px",
                    }),
                    valueContainer: (provided) => ({
                      ...provided,
                      height: "32px",
                      padding: "0 6px",
                    }),
                    indicatorsContainer: (provided) => ({
                      ...provided,
                      height: "32px",
                    }),
                    option: (provided) => ({
                      ...provided,
                      fontSize: "14px",
                      padding: "6px 12px",
                      minHeight: "28px",
                    }),
                    menuList: (provided) => ({
                      ...provided,
                      paddingTop: 0,
                      paddingBottom: 0,
                    }),
                  }}
                  menuPlacement="top"
                />
              </div>
            </Card.Footer>
          </Card>
        </div>
      )}
    </>
  );
};

export default TranchesTable;
